import { MessageTypeColor } from "@/types";
import {
  botMessageColors,
  botMessageColorsDark,
  userMessageColors,
  userMessageColorsDark,
} from "../../../../colors/colors";
import { useTheme } from "@mui/material/styles";

export const useFlippingCardStyles = (
  isBot: boolean,
  variant: MessageTypeColor = "default",
  embedded = false,
) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const messageColors = isBot
    ? isDark
      ? botMessageColorsDark
      : botMessageColors
    : isDark
      ? userMessageColorsDark
      : userMessageColors;

  const colors = messageColors[variant] || messageColors.default;

  return {
    container: {
      position: "relative",
      borderRadius: 3,
      display: "flex",
      flexDirection: "column",
      gap: 1,
      width: "100%",
      boxSizing: "border-box",
      // Embedded: full width on xs (avoid overflow); from sm — same 500px-centered footprint as standalone.
      ...(embedded
        ? {
            maxWidth: "100%",
            minWidth: 0,
            margin: 0,
            alignSelf: "stretch",
            [theme.breakpoints.up("sm")]: {
              maxWidth: "500px",
              width: "100%",
              marginLeft: "auto",
              marginRight: "auto",
              alignSelf: "center",
            },
          }
        : {
            minWidth: { xs: "260px", sm: "500px", md: "500px", lg: "500px" },
            maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: "95%" },
            margin: "0 auto",
          }),
    },
    cardSide: {
      position: "absolute",
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      padding: 3,
      borderRadius: 4,
      backgroundColor:
        theme.palette.footer?.menuDropdown?.bgColor ||
        theme.palette.background.paper,
      boxShadow: isDark
        ? "0 2px 8px rgba(0, 0, 0, 0.3)"
        : "0 2px 8px rgba(0, 0, 0, 0.1)",
      cursor: "pointer",
      minHeight: "250px",
      maxHeight: "400px",
      border: isDark
        ? `1px solid ${theme.palette.grey[800]}`
        : `1px solid ${theme.palette.grey[300]}`,
      overflow: "hidden",
      boxSizing: "border-box",
      [theme.breakpoints.down("sm")]: embedded
        ? {
            pt: 2.5,
            px: 1.5,
            pb: 1.5,
            minHeight: "200px",
            maxHeight: "280px",
          }
        : {
            pt: 2.5,
            px: 2,
            pb: 2,
            minHeight: "200px",
            maxHeight: "300px",
          },
    },
    cardSideBack: {
      position: "absolute",
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      padding: 3,
      borderRadius: 4,
      backgroundColor: isDark ? "#FFF9C4" : "#FFFDE7",
      boxShadow: isDark
        ? "0 2px 8px rgba(0, 0, 0, 0.3)"
        : "0 2px 8px rgba(0, 0, 0, 0.1)",
      cursor: "pointer",
      minHeight: "250px",
      maxHeight: "400px",
      border: isDark ? "1px solid #FDD835" : "1px solid #FFEB3B",
      overflow: "hidden",
      boxSizing: "border-box",
      [theme.breakpoints.down("sm")]: embedded
        ? {
            pt: 2.5,
            px: 1.5,
            pb: 1.5,
            minHeight: "200px",
            maxHeight: "280px",
          }
        : {
            pt: 2.5,
            px: 2,
            pb: 2,
            minHeight: "200px",
            maxHeight: "300px",
          },
    },
    title: {
      fontFamily: '"Red Hat Display", sans-serif',
      fontWeight: 700,
      color: colors.text,
      textAlign: "center",
      fontSize: "22px",
      marginTop: 0,
      flexShrink: 0,
      marginBottom: embedded ? "-12px" : "-30px",
      px: embedded ? 0.5 : 0,
      [theme.breakpoints.down("sm")]: {
        fontSize: embedded ? "15px" : "17px",
        lineHeight: 1.25,
        marginBottom: embedded ? "-2px" : "-4px",
      },
    },
    text: {
      fontFamily: '"Red Hat Display", sans-serif',
      color: isDark ? theme.palette.text.primary : theme.palette.text.primary,
      fontSize: "20px",
      lineHeight: 1.5,
      fontWeight: 700,
      wordWrap: "break-word",
      overflowWrap: "break-word",
      maxWidth: "100%",
      [theme.breakpoints.down("sm")]: {
        fontSize: embedded ? "13px" : "14px",
        lineHeight: 1.45,
      },
    },
    navButton: {
      backgroundColor: isDark
        ? theme.palette.grey[700]
        : theme.palette.grey[200],
      color: isDark ? theme.palette.text.primary : theme.palette.text.primary,
      borderRadius: "50%",
      "&:hover": {
        backgroundColor: isDark
          ? theme.palette.grey[600]
          : theme.palette.grey[300],
      },
      "&:disabled": {
        backgroundColor: isDark
          ? theme.palette.grey[800]
          : theme.palette.grey[100],
        color: theme.palette.text.disabled,
      },
      width: 40,
      height: 40,
      padding: 0,
      [theme.breakpoints.down("sm")]: {
        width: 36,
        height: 36,
      },
    },
    counter: {
      fontFamily: '"Red Hat Display", sans-serif',
      fontWeight: 500,
      color: isDark ? theme.palette.text.primary : theme.palette.text.secondary,
      fontSize: "14px",
      minWidth: "50px",
      textAlign: "center",
      backgroundColor: isDark
        ? theme.palette.grey[700]
        : theme.palette.grey[100],
      borderRadius: 5,
      padding: "6px 12px",
      border: isDark
        ? `1px solid ${theme.palette.grey[600]}`
        : `1px solid ${theme.palette.grey[300]}`,
      [theme.breakpoints.down("sm")]: {
        fontSize: embedded ? "12px" : "13px",
        padding: embedded ? "4px 10px" : "6px 11px",
      },
    },
  };
};
